import { useState, useRef }                                from 'react';
import { Dialog, DialogBackdrop, DialogPanel,
         Popover, PopoverButton, PopoverPanel }            from '@headlessui/react';

import StrategicChart                                      from '../GoogleCharts/StrategicChart';
import GanttChart                                          from '../GoogleCharts/Gantt';
import ExportButtons                                       from '../Buttons/ExportButtons';
import FeedbackModal                                       from '../Modals/FeedbackModal';
import { useUser }                                         from '../../contexts/UserContext';
import { useMassBalancePage }                              from '../../contexts/MassBalancePageContext';
import { findPermission }                                  from '../../utils/UserPermissionUtils.js';


const HEATMAP_THRESHOLDS = { green: 75, yellow: 95 };

const COR_CONFORTO = '#2ecc71';
const COR_ATENCAO  = '#f1c40f';
const COR_RISCO    = '#e74c3c';

const PARADAS_GRADIENTE = [
  { valor: 0,                        cor: COR_CONFORTO },
  { valor: HEATMAP_THRESHOLDS.green,  cor: COR_ATENCAO },
  { valor: HEATMAP_THRESHOLDS.yellow, cor: COR_RISCO },
];

function formatarDataIsoParaBr(dataIso) {
  if (!dataIso) return '';
  const [ano, mes, dia] = dataIso.split('-');
  return `${dia}/${mes}/${ano}`;
}

function formatarDataCurtaIsoParaBr(dataIso) {
  if (!dataIso) return '';
  const [, mes, dia] = dataIso.split('-');
  return `${dia}/${mes}`;
}

function corParaValor(v) {
  if (v <= HEATMAP_THRESHOLDS.green)  return COR_CONFORTO;
  if (v < HEATMAP_THRESHOLDS.yellow)  return COR_ATENCAO;
  return COR_RISCO;
}

function hexParaRgb(hex) {
  const n = parseInt(hex.replace('#', '').slice(0, 6), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

function corGradualParaValor(v) {
  const primeira = PARADAS_GRADIENTE[0];
  const ultima = PARADAS_GRADIENTE[PARADAS_GRADIENTE.length - 1];
  if (v <= primeira.valor) return primeira.cor;
  if (v >= ultima.valor) return ultima.cor;

  const i = PARADAS_GRADIENTE.findIndex((p) => v < p.valor);
  const a = PARADAS_GRADIENTE[i - 1];
  const b = PARADAS_GRADIENTE[i];
  const t = (v - a.valor) / (b.valor - a.valor);

  const [r1, g1, b1] = hexParaRgb(a.cor);
  const [r2, g2, b2] = hexParaRgb(b.cor);
  const mix = (x, y) => Math.round(x + (y - x) * t);
  return `rgb(${mix(r1, r2)}, ${mix(g1, g2)}, ${mix(b1, b2)})`;
}

function corTextoParaFundo(cor) {
  const [r, g, b] = cor.startsWith('#')
    ? hexParaRgb(cor)
    : cor.match(/\d+/g).map(Number);
  const luminancia = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminancia > 0.6 ? '#1f2937' : '#ffffff';
}

function Toggle({ label, ativo, onChange }) {
  return (
    <div className="flex items-center justify-between gap-6 px-3 py-2">
      <span className="text-xs text-gray-700 whitespace-nowrap">{label}</span>
      <button
        type="button"
        role="switch"
        aria-checked={ativo}
        onClick={() => onChange(!ativo)}
        className={`relative inline-flex h-6 w-10 shrink-0 items-center rounded-full transition-colors ${ativo ? 'bg-azul-profundo-m' : 'bg-gray-300'}`}
      >
        <span
          className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${ativo ? 'translate-x-[18px]' : 'translate-x-[2px]'}`}
        />
      </button>
    </div>
  );
}

function IconeEngrenagem() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5">
      <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function MassBalanceHeatmapTable({ dados = [], colunas = [], loading = false }) {
  const [linhaSelecionada, setLinhaSelecionada] = useState(null);
  const [mostrarPercentual, setMostrarPercentual] = useState(true);
  const [modoGradual, setModoGradual] = useState(true);
  const [mostrarBordas, setMostrarBordas] = useState(true);
  const [exportFeedback, setExportFeedback] = useState(null);

  const { userData }     = useUser();
  const { selectedDate } = useMassBalancePage();
  const exportRef        = useRef(null);

  // Exportar JPEG/PDF exige a permissão de edição do Balanço de Massa.
  const podeExportar = !!findPermission(userData, 'Edição Balanço de Massa') && dados.length > 0;

  const diasRepresentativosSelecionados = (linhaSelecionada?.diasRepresentativos || []).filter(Boolean);
  const registrosSelecionados = linhaSelecionada?.registros || [];

  const strategicChartData = diasRepresentativosSelecionados.map((linha)  => ({
    date: linha.date,
    value: Number(linha.final),
    static: Number(linha.static),
    road: Number(linha.road),
    railway: Number(linha.railway),
  }));

  const ganttData = registrosSelecionados.map((linha) => ({
    ship: linha.ship,
    date: linha.date,
  }));

  return (
    <div ref={exportRef} className="bg-white borda-cinza shadow-sm rounded-[8px] overflow-hidden">
      <div className="flex justify-between items-start px-5 py-3">
        <div>
          <p className="text-sm font-semibold">Heatmap | Balanço de Massa</p>
          <span className="block w-11 h-1 bg-[#2e5395] mt-1" />
        </div>

        <div className="flex items-center gap-3" data-export-ignore>

        {podeExportar && (
          <ExportButtons
            size="sm"
            getTarget={() => exportRef.current}
            fileName={`heatmap-balanco-de-massa-${selectedDate || 'atual'}`}
            title="Heatmap | Balanço de Massa"
            onError={(message) => setExportFeedback({ type: 'error', title: 'Erro ao exportar o heatmap', message })}
          />
        )}

        <Popover className="relative">
          <PopoverButton
            title="Configurações da tabela"
            className="flex h-8 w-8 items-center justify-center rounded-full text-gray-600 hover:bg-gray-100 focus:outline-none data-[open]:bg-gray-100"
          >
            <IconeEngrenagem />
          </PopoverButton>

          <PopoverPanel
            anchor="bottom end"
            transition
            className="z-[50] mt-2 min-w-[220px] rounded-lg bg-white py-1 shadow-lg ring-1 ring-black/5 transition data-[closed]:opacity-0 data-[closed]:scale-95"
          >
            <Toggle label="Mostrar percentual" ativo={mostrarPercentual} onChange={setMostrarPercentual} />
            <Toggle label="Cores graduais"     ativo={modoGradual}       onChange={setModoGradual} />
            <Toggle label="Separar linhas"     ativo={mostrarBordas}     onChange={setMostrarBordas} />
          </PopoverPanel>
        </Popover>

        </div>
      </div>

      {dados.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="w-full xl:table-fixed  text-sm">
            <thead>
              <tr>
                <th
                  title="TERMINAL"
                  className=" bg-azul-profundo-m text-white px-3 py-2 rounded-tl-[8px] w-[100px] sticky left-0 whitespace-nowrap overflow-hidden text-ellipsis"
                >
                  TERMINAL
                </th>
                <th
                  title="PRODUTO"
                  className=" bg-azul-profundo-m text-white px-3 py-2 w-[100px] sticky left-[100px] whitespace-nowrap overflow-hidden text-ellipsis"
                >
                  PRODUTO
                </th>
                {colunas.map((data, i) => (
                  <th
                    key={data}
                    title={formatarDataIsoParaBr(data)}
                    className={` bg-azul-profundo-m text-white px-1 py-2 text-xs whitespace-nowrap xl:px-2 2xl:px-3 2xl:text-sm ${i === colunas.length - 1 ? 'rounded-tr-[8px]' : ''}`}
                  >
                    {formatarDataCurtaIsoParaBr(data)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {dados.map((linha, i) => {
                const classeBorda = mostrarBordas && i < dados.length - 1 ? 'border-b border-gray-200' : '';
                return (
                <tr key={`${linha.terminal}-${linha.produto}-${i}`}>
                  <td
                    title={linha.terminal}
                    className={` bg-[#dbe5f1] font-semibold text-center px-3 py-2 cursor-pointer hover:underline w-[100px] sticky left-0 whitespace-nowrap overflow-hidden text-ellipsis ${classeBorda} ${i === dados.length - 1 ? 'rounded-bl-[8px]' : ''}`}
                    onClick={() => setLinhaSelecionada(linha)}
                  >
                    {linha.terminal}
                  </td>
                  <td
                    title={linha.produto}
                    className={` bg-[#dbe5f1] text-center px-3 py-2 w-[100px] sticky left-[100px] whitespace-nowrap overflow-hidden text-ellipsis ${classeBorda}`}
                  >
                    {linha.produto}
                  </td>
                  {linha.valores.map((v, j) => (
                    <td
                      key={j}
                      title={v === null || v === undefined ? 'Sem dado postado' : `${v.percentual.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`}
                      className={` text-center px-1 py-2 text-xs whitespace-nowrap xl:px-2 2xl:px-3 2xl:text-sm ${classeBorda} ${i === dados.length - 1 && j === linha.valores.length - 1 ? 'rounded-br-[8px]' : ''}`}
                      style={(() => {
                        if (v === null || v === undefined) return { backgroundColor: '#f2f2f2' };
                        const fundo = modoGradual ? corGradualParaValor(v.percentual) : corParaValor(v.percentual);
                        return { backgroundColor: fundo, color: corTextoParaFundo(fundo) };
                      })()}
                    >
                      {v === null || v === undefined || !mostrarPercentual
                        ? ''
                        : `${v.percentual.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}%`}
                    </td>
                  ))}
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="px-5 py-4 text-sm text-gray-500">
          {loading ? 'Carregando...' : 'Selecione um terminal para ver o balanço de massa.'}
        </p>
      )}

      <div className="px-5 py-2 text-xs text-gray-500">Público</div>

      <FeedbackModal feedback={exportFeedback} onClose={() => setExportFeedback(null)} />

      <Dialog
        open={!!linhaSelecionada}
        onClose={() => setLinhaSelecionada(null)}
        className={linhaSelecionada ? 'relative z-[100]' : 'hidden'}
      >
        <DialogBackdrop
          onClick={() => setLinhaSelecionada(null)}
          transition
          className="fixed inset-0 bg-bg-backdrop transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
        />

        <div className="fixed inset-0 z-10 w-screen">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <DialogPanel
              transition
              className="min-w-[80%] max-w-[80%] relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in sm:my-8 data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95"
            >
              <div className="app-container">
                <header className="bg-azul-profundo-m flex justify-between items-center pl-4 pr-1 py-3">
                  <h2 className="text-white text-[1rem] leading-7 font-semibold">
                    {linhaSelecionada ? `${linhaSelecionada.terminal} | ${linhaSelecionada.produto}` : ''}
                  </h2>
                  <button
                    className="text-[1.2rem] text-white hover:text-gray-200 pr-2"
                    onClick={() => setLinhaSelecionada(null)}
                  >
                    ×
                  </button>
                </header>

                <div className="max-h-[70vh] overflow-y-auto overflow-x-hidden p-[15px]">
                  <section className="strategic-chart w-full bg-neutro borda-cinza rounded-[7px] p-4 mb-4">
                    <h2 className="text-[1.1rem] mb-1">Visão Estratégica</h2>
                    <p className="text-[0.8rem] text-cinza-chumbo-m mb-3">Evolução do volume final no período selecionado.</p>
                    <StrategicChart data={strategicChartData} loading={false} />
                  </section>

                  <section className="w-full bg-neutro borda-cinza rounded-[7px] p-4 mb-4">
                    <h2 className="text-[1.1rem] mb-1">Descarga de Navios</h2>
                    <p className="text-[0.8rem] text-cinza-chumbo-m mb-3">Período de descarga dos Navios</p>
                    <GanttChart data={ganttData} loading={false} />
                  </section>
                </div>
              </div>
            </DialogPanel>
          </div>
        </div>
      </Dialog>
    </div>
  );
}

export default MassBalanceHeatmapTable;
export { HEATMAP_THRESHOLDS, corParaValor, corGradualParaValor };